/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/naming-convention */
import { Box } from '@mui/material'
import React, { lazy, Suspense, useEffect, useState } from 'react'
import LoadingPage from '../../../pages/LoadingPage'
import { getCourseLoading, getSlotSections } from '../../../store/slices/distributionPlanSlice/selectors'
import { CreateAndEditCourses } from './CreateAndEditCourses'
import { SlotStatus, type IDistributionPlanList, type ISlot } from '../../../models/DistributionPlan'
import { type Row, type ApplyFiltersAndDispatchParams, Actions } from '../../../components/CustomTable/types'
import { useAppDispatch } from '../../../store'
import { getSlotSectionApi } from '../../../store/slices/distributionPlanSlice/apis'
import { useParams, useSearchParams } from 'react-router-dom'
import CoursesTopbarActions from './CoursesTopbarActions'
import CourseDelete from './CourseDelete'
import ReopenSurveyModal, { type ReopenSurveyPayload } from './ReopenSurveyModal'
import { CloseSurveyModal } from './CloseSurveyModal'
import { GoLiveSectionModal } from './GoLiveSectionModal'
import { UpdateTemplateRevisionModal } from './UpdateTemplateRevisionModal'
import { createCloseSurveySuccessHandler } from './closeSurveyHandlers'
import { createReopenSurveySuccessHandler } from './reopenSurveyHandlers'
import { shouldSwitchSectionView } from './sectionViewSwitch'
import { createCourseExportSuccessHandler } from './exportHandlers'
import { createUpdateTemplateRevisionSuccessHandler } from './templateRevisionHandlers'
import { notification, closeNotification } from '../../../components/Notifications'
import ROUTES from '../../../consts/paths'
import { type ISectionsList, type ISectionRow, type ISection } from '../../../models/TermsSection'
import CustomHeader from '../../../components/CustomHeader'
import { useMutation } from '@tanstack/react-query'
import { AutoImportManager, DistributionManager } from '../../../APIManager'
import { type TableCell } from '../../../models/CompletionRate'
import { useAppSearchParams } from '../../../hooks/useAppSearchParams'
import { useSlotTaskContext } from '../../../contexts/SlotTaskContext'
import { viewFromStatus } from '../ProjectSectionTab'
import { isInstructorReleaseOnGradePostingEnabled } from '../../../consts/messages'
import { formatSectionReleaseDateCell } from './releaseDateDisplay'
import { formatDateByUserTimeZone } from '../../../helpers/formatDateByUserTimeZone'
import CustomTooltip from '../../../components/CustomTooltip'
import { getOptedOutCellState } from './optedOutColumn'
import { resolveInstructorOptOutSectionId } from './instructorOptOutSectionId'
import InstructorOptOutModal, { type InstructorOptOutEntry } from './InstructorOptOutModal'

const AppTable = lazy(async () => await import('../../../components/CustomTable'))

// Section warnings the backend keeps beside the column warning. `surveyWithdrawn`: an update left
// the section with no questions, so students no longer see its survey; cleared once questions return.
// `responsesHidden`: a template revision push stopped submitted answers showing in reports; never cleared.
const ROW_LEVEL_WARNING_KEYS = ['surveyWithdrawn', 'responsesHidden']
interface CoursesProps {
  slot: ISlot
}
interface GoLiveParams {
  slotId: string
  sectionId: string
}

const Courses: React.FC<CoursesProps> = ({ slot }) => {
  const { searchParams, setUrlParams } = useAppSearchParams()
  const searchValue = searchParams.get('search')
  const selectedView = searchParams.get('view')
  const scope = searchParams.get('scope') as 'my' | 'all'
  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({ search: searchValue || '', scope: scope ?? 'my', pageSize: 10 })
  const { setTask, checkIfBlocked, isTaskRunning } = useSlotTaskContext()

  const { id } = useParams()
  const dispatch = useAppDispatch()

  const isCourseLoading = getCourseLoading()
  const slotSections = getSlotSections()
  const headerName = slotSections?.header?.headerName || 'Course Sections Surveys'

  const isSlotSections = (slotSections?.data?.length ?? 0) > 0

  const isGradePostingReleaseEnabled = isInstructorReleaseOnGradePostingEnabled(slot?.isInstructorReleaseOnGradePostingEnabled)

  const slotSectionsWithModifiedActions = {
    ...slotSections,
    data: slotSections?.data?.map((section: ISectionRow) => {
      const { slotStatus, earlyAccessReleaseTimestamp_1, earlyAccessReleaseTimestamp_2 } = section.data || {}

      return {
        ...section,
        data: {
          ...section.data,
          earlyAccessReleaseTimestamp_1: earlyAccessReleaseTimestamp_1 ?? slot?.releaseTimestamp,
          earlyAccessReleaseTimestamp_2: isGradePostingReleaseEnabled
            ? earlyAccessReleaseTimestamp_2
            : (earlyAccessReleaseTimestamp_2 ?? slot?.releaseTimestamp),
          isInstructorOptInOverrideEnabled: slot?.isInstructorOptInOverrideEnabled
        }
      }
    })
  }

  useEffect(() => {
    if (selectedView && id) {
      void dispatch(getSlotSectionApi({ urlId: id, params: { ...params, view: selectedView } }))
    }
  }, [id, params, selectedView])

  const handleExportSuccess = createCourseExportSuccessHandler({ setTask })

  const handleExportError = (error: any, _variables: any, context: any): void => {
    closeNotification(context)
    notification(error?.message ?? 'Export csv failed', { variant: 'error' })
  }

  const sharedExportMutationOptions = {
    onMutate: () => notification('Preparing download...', { variant: 'loading', persist: true }),
    onSuccess: handleExportSuccess,
    onError: handleExportError
  }

  const { mutate: exportSectionCSVMutate, isPending: exportLoading } = useMutation({
    mutationFn: DistributionManager.exportSectionsCSV,
    ...sharedExportMutationOptions
  })

  const { mutate: exportEnrollmentsCSVMutate } = useMutation({
    mutationFn: DistributionManager.exportEnrollmentsCSV,
    ...sharedExportMutationOptions,
    meta: { silent: true }

  })

  const { mutate: exportInstructorsAssignmentsCSVMutate } = useMutation({
    mutationFn: DistributionManager.exportInstructorsAssignmentsCSV,
    ...sharedExportMutationOptions,
    meta: { silent: true }

  })

  const { mutate: exportStudentSurveySubmissionMutate } = useMutation({
    mutationFn: DistributionManager.exportStudentSurveySubmission,
    ...sharedExportMutationOptions,
    meta: { silent: true }

  })

  // Guarded wrappers — block if a background task is running; instantly verify status via API on click
  const guardedExportEnrollments = (exportParams?: Record<string, any>): void => {
    void checkIfBlocked().then((blocked) => { if (!blocked) exportEnrollmentsCSVMutate(exportParams) })
  }

  const guardedExportInstructorsAssignments = (exportParams?: Record<string, any>): void => {
    void checkIfBlocked().then((blocked) => { if (!blocked) exportInstructorsAssignmentsCSVMutate(exportParams) })
  }

  const guardedExportStudentSurveySubmission = (slotId: string, exportParams?: Record<string, any>): void => {
    void checkIfBlocked().then((blocked) => { if (!blocked) exportStudentSurveySubmissionMutate(slotId, exportParams) })
  }

  const handleExport = (): void => {
    void checkIfBlocked().then((blocked) => {
      if (blocked) return
      const exportParams = {
        slotId: slot.slotId,
        search: params?.search || '',
        sort: typeof params?.sort === 'string' ? params.sort : undefined,
        pageSize: params?.pageSize || 10
      }
      if (slotSections?.data.length === 0) {
        notification('No course sections available to export.', { variant: 'info' })
        return
      }
      exportSectionCSVMutate(exportParams)
    })
  }

  const handleSurveyPreview = (row: IDistributionPlanList | Row): void => {
    const selectedRowData = row.data
    const surveyPreviewUrl = `${ROUTES.SURVEY_PREVIEW}/${selectedRowData?.slotId}/${selectedRowData?.linkedSection?.sectionId}`
    window.open(surveyPreviewUrl, '_blank')
  }

  const { mutateAsync: handleGoLive, isPending: goLiveLoading } = useMutation({
    mutationFn: async (params: GoLiveParams) => {
      return await DistributionManager.goLiveSectionApi(params)
    },
    onSuccess: async (data) => {
      notification(data?.data?.message ?? 'Section successfully live', { variant: 'success' })
      await dispatch(getSlotSectionApi({ urlId: id, params: { ...params, ...(selectedView ? { view: selectedView } : {}) } }))
    },
    onError: (error: any) => {
      notification(error?.response?.data?.error ?? 'Internal server error', { variant: 'error' })
    }
  })

  const { mutate: handleOptoutOrOptIn, isPending: isOptoutOrOptInPinding } = useMutation({
    mutationFn: AutoImportManager.updateOptOutOrOptIn,
    onError: (error) => {
      notification(error?.message ?? 'Opt out or opt In error', { variant: 'error' })
    },
    meta: { silent: true },
    onSuccess: async (data) => {
      notification(data.data.isOptedOut ? 'Opt out successful' : 'Opt in successful', { variant: 'success' })
      await dispatch(getSlotSectionApi({ urlId: id, params }))
    }
  })

  const handleOptOut = (row?: Row): void => {
    void checkIfBlocked().then((blocked) => {
      if (blocked) return
      const payload = {
        params: {
          slotId: slot.slotId,
          objectId: row?.data?.linkedSection?.sectionId
        },
        body: {
          isOptedOut: !row?.data?.isOptedOut
        }
      }
      handleOptoutOrOptIn(payload)
    })
  }

  const [instructorOptOutRow, setInstructorOptOutRow] = useState<Row | null>(null)

  const { mutate: handleInstructorOptOutMutate, isPending: isInstructorOptOutPending } = useMutation({
    mutationFn: AutoImportManager.updateInstructorOptOut,
    onError: (error: any) => {
      notification(error?.message ?? 'Opt Out Instructors error', { variant: 'error' })
    },
    meta: { silent: true },
    onSuccess: async () => {
      notification('Instructor opt-out updated successfully', { variant: 'success' })
      setInstructorOptOutRow(null)
      await dispatch(getSlotSectionApi({ urlId: id, params }))
    }
  })

  const handleOptOutInstructorClick = (row?: Row): void => {
    if (row) setInstructorOptOutRow(row)
  }

  const handleConfirmInstructorOptOut = (instructors: InstructorOptOutEntry[]): void => {
    const sectionId = resolveInstructorOptOutSectionId(instructorOptOutRow)
    if (!sectionId) {
      notification('Course section not found for this row.', { variant: 'error' })
      return
    }
    void checkIfBlocked().then((blocked) => {
      if (blocked) return
      handleInstructorOptOutMutate({
        params: {
          slotId: slot.slotId,
          sectionId
        },
        body: { instructors }
      })
    })
  }

  // Reads `view` from URL at call time so async-task callbacks pick up tab changes that happen
  // between task start and task completion (e.g., status-driven tab flip after a close).
  // Filter state (search/scope/pageSize) is captured from the click-time render.
  const refreshSections = (): void => {
    const currentView = new URLSearchParams(window.location.search).get('view')
    void dispatch(getSlotSectionApi({ urlId: id, params: { ...params, ...(currentView ? { view: currentView } : {}) } }))
  }

  // Refresh the table for a specific destination view (used when a row action moves a section to
  // another status view). Guarantees exactly one refetch: when the view changes, the
  // view-watching effect refetches; when `targetView` is already active — so `setUrlParams` is a
  // no-op and the effect would not fire — we dispatch the fetch directly. Reads the live view
  // from the URL so async background-task callbacks stay correct after the click-time render.
  const refreshSectionsForView = (targetView: string): void => {
    const currentView = new URLSearchParams(window.location.search).get('view')
    setUrlParams({ view: targetView })
    if (currentView === targetView) {
      void dispatch(getSlotSectionApi({ urlId: id, params: { ...params, view: targetView } }))
    }
  }

  const liveAndUpcomingView = viewFromStatus(SlotStatus.Live)
  const closedView = viewFromStatus(SlotStatus.Closed)

  const { mutateAsync: closeSurveyMutate, isPending: closeSurveyLoading } = useMutation({
    mutationFn: async ({ slotId, sectionId }: { slotId: string, sectionId: string, wasLastLiveSection: boolean }) =>
      await DistributionManager.closeSurveySectionApi({ slotId, sectionId }),
    onSuccess: createCloseSurveySuccessHandler({
      setTask,
      refreshSections,
      // Current (Live & Upcoming) view just lost its only section — show the closed section instead.
      refreshClosedView: () => { refreshSectionsForView(closedView) }
    }),
    onError: (error: any) => {
      notification(error?.response?.data?.error ?? 'Unable to close survey', { variant: 'error' })
    }
  })

  // Returns false when the action was blocked (a background task is running) so the modal stays
  // open for the user to retry; true once the close request has been issued.
  const handleCloseSurvey = async (row?: ISection | null): Promise<boolean> => {
    if (await checkIfBlocked()) return false
    const slotId = row?.slotId
    const sectionId = row?.linkedSection?.sectionId
    if (slotId && sectionId) {
      const wasLastLiveSection = shouldSwitchSectionView(selectedView, liveAndUpcomingView, slotSections?.pagination?.totalResults)
      await closeSurveyMutate({ slotId, sectionId, wasLastLiveSection })
    }
    return true
  }

  const { mutate: updateTemplateRevisionMutate, isPending: updateTemplateRevisionLoading } = useMutation({
    mutationFn: DistributionManager.updateTemplateRevision,
    onSuccess: createUpdateTemplateRevisionSuccessHandler({ setTask, refreshSections }),
    onError: (error: any) => {
      notification(error?.message ?? 'Unable to update survey template revision', { variant: 'error' })
    },
    meta: { silent: true }
  })

  const handleUpdateTemplateRevision = (row?: ISection | null): void => {
    void checkIfBlocked().then((blocked) => {
      if (blocked) return
      const distributionId = row?.distributionId
      if (!distributionId) {
        notification('Unable to update survey template revision because this course section is missing a distribution ID.', { variant: 'error' })
        return
      }
      updateTemplateRevisionMutate({ distributionId })
    })
  }

  const { mutateAsync: reopenSurveyMutate, isPending: reopenSurveyLoading } = useMutation({
    // ReopenSurveyModal owns this mutation's error UI: it renders the 422 boundary-date
    // validation inline and as an enriched, localized toast. Opt out of the global
    // mutation-cache error toast (ReactQueryProvider) so the user doesn't also get a
    // second toast showing the raw first field value (a bare UTC timestamp).
    meta: { silent: true },
    mutationFn: async ({ payload }: { payload: ReopenSurveyPayload, wasLastClosedSection: boolean }) =>
      await DistributionManager.reopenSectionSurvey(payload),
    onSuccess: createReopenSurveySuccessHandler({
      refreshSections,
      // Closed view just lost its only section — show the reopened section in Live & Upcoming.
      refreshLiveView: () => { refreshSectionsForView(liveAndUpcomingView) }
    })
  })

  // Returns false when the action was blocked or the row is invalid so the modal stays open; true
  // once the go-live request has been issued.
  const handleGoLIVE = async (row?: ISection | null): Promise<boolean> => {
    if (await checkIfBlocked()) return false
    const slotId = row?.slotId
    const sectionId = row?.linkedSection?.sectionId
    if (!slotId || !sectionId) {
      console.error('Error: Invalid row data. Either slotId or sectionId is missing.')
      notification('Invalid row data. Please check the Survey Project instance ID and section ID.', { variant: 'error' })
      return false
    }
    await handleGoLive({ slotId, sectionId })
    return true
  }
  const allowedActions = slotSectionsWithModifiedActions?.actions ?? []

  const handleReopenSurvey = async (payload: ReopenSurveyPayload): Promise<void> => {
    if (await checkIfBlocked()) return
    if (!id) return
    const wasLastClosedSection = shouldSwitchSectionView(selectedView, closedView, slotSections?.pagination?.totalResults)
    await reopenSurveyMutate({ payload, wasLastClosedSection })
  }

  const renderReleaseDateCell = (value: string | null | undefined, isInstructorColumn: boolean): string =>
    formatSectionReleaseDateCell(value, { isInstructorColumn, isGradePostingReleaseEnabled })

  const extraColumnProps = [
    {
      key: 'earlyAccessReleaseTimestamp_1',
      label: 'Admin Release Date',
      render: (row: Row) => renderReleaseDateCell(row.data?.earlyAccessReleaseTimestamp_1, false)
    },
    {
      key: 'earlyAccessReleaseTimestamp_2',
      label: 'Instructor Release Date',
      render: (row: Row) => renderReleaseDateCell(row.data?.earlyAccessReleaseTimestamp_2, true)
    }
  ]

  // Cell content renders via `overrides` (consumed by generateCell), not `extraColumnProps.render`.
  // When grade-posting release is on, show the section instructor release date or "Unset" when
  // it has been left empty, instead of a blank cell.
  const overrides = {
    isOptedOut: (cell: TableCell) => {
      const cellState = getOptedOutCellState(cell.isEffectivelyFullyOptedOut, cell.optedOutInstructors, {
        legacyIsOptedOut: cell.isOptedOut
      })
      if (cellState.status === 'yes') return <>Yes</>
      if (cellState.status === 'no') return <>No</>
      return (
        <CustomTooltip title={cellState.fullText}>
          <span>{cellState.visibleText}</span>
        </CustomTooltip>
      )
    },
    ...(isGradePostingReleaseEnabled
      ? {
          earlyAccessReleaseTimestamp_2: (cell: TableCell) => {
            const raw = cell.earlyAccessReleaseTimestamp_2
            const rawStr = typeof raw === 'string' ? raw : null
            const formattedDate = formatDateByUserTimeZone(rawStr, 'MM/dd/yyyy @ h:mm a')
            return <>{formatSectionReleaseDateCell(formattedDate || null, { isInstructorColumn: true, isGradePostingReleaseEnabled })}</>
          }
        }
      : {})
  }

  return (
    <Box>
      <Suspense fallback={<LoadingPage />}>
        <CustomHeader info={`${slot.term.termName} | ${slot?.title} `} />
        <AppTable
          key={'projectCourseSections'}
          exportButtonProps={{ loading: exportLoading, disabled: exportLoading }}
          loading={isCourseLoading}
          list={slotSectionsWithModifiedActions as ISectionsList}
          noDataComponentProps={slot?.slotNodeDetails?.slotStatus === SlotStatus.Draft
            ? {
                title: ' 1. Start by importing your courses',
                subTitle: 'Survey Projects cannot be published until you have uploaded your courses.'
              }
            : undefined}
          customTopbarAction={
            <CoursesTopbarActions
              exportEnrollmentsCSVMutate={guardedExportEnrollments}
              exportInstructorsAssignmentsCSVMutate={guardedExportInstructorsAssignments}
              exportStudentSurveySubmissionMutate={guardedExportStudentSurveySubmission}
              handleExport={handleExport}
              slot={slot}
              params={params}
              isSlotSections={isSlotSections}
              allowedActions={allowedActions}
              refreshSectionsForView={refreshSectionsForView} />}
          featureName='Course Section Assignment'
          editAction={<CreateAndEditCourses slot={slot} />}
          deleteAction={<CourseDelete params={params} slot={slot} />}
          reopenSurveyAction={<ReopenSurveyModal onSubmit={handleReopenSurvey} isSubmitting={reopenSurveyLoading} />}
          closeSurveyAction={<CloseSurveyModal onConfirm={handleCloseSurvey} isSubmitting={closeSurveyLoading} />}
          goLiveAction={<GoLiveSectionModal onConfirm={handleGoLIVE} isSubmitting={goLiveLoading} />}
          updateTemplateRevisionAction={<UpdateTemplateRevisionModal onConfirm={handleUpdateTemplateRevision} isSubmitting={updateTemplateRevisionLoading} />}
          disabledRowActions={isTaskRunning ? [Actions.UPDATE_TEMPLATE_REVISION] : []}
          rowLevelWarningKeys={ROW_LEVEL_WARNING_KEYS}
          disabledRowActionTooltip='A survey template revision update is already in progress for this Survey Project. Please wait for it to finish.'
          params={params}
          setParams={setParams}
          onPreviewClick={handleSurveyPreview}
          extraColumnProps={extraColumnProps}
          onOptOutClick={handleOptOut}
          onOptOutInstructorClick={handleOptOutInstructorClick}
          showScopeToggle={true}
          scopeToggleTitle={headerName}
          overrides={overrides}
        />
        {instructorOptOutRow && (
          <InstructorOptOutModal
            row={instructorOptOutRow}
            onClose={() => { setInstructorOptOutRow(null) }}
            onConfirm={handleConfirmInstructorOptOut}
            isSubmitting={isInstructorOptOutPending}
          />
        )}
      </Suspense>
    </Box>
  )
}

export default Courses
